// Minimal Razorpay Checkout (web) loader + promise wrapper.

/** Structural shape of a created payment order — satisfied by both the metro
 *  PaymentOrderResponse and the generated PassPaymentOrderResponse. */
interface RazorpayOrderLike {
  razorpayOrderId: string;
  keyId: string;
  amountPaise: number;
  currency: string;
}

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayPrefill {
  name?: string;
  contact?: string;
  email?: string;
}

interface RazorpayOptions {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  prefill?: RazorpayPrefill;
  theme?: { color?: string };
  handler: (response: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: string, cb: (resp: unknown) => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let loaderPromise: Promise<void> | null = null;

function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Failed to load Razorpay Checkout'));
    };
    document.body.appendChild(script);
  });
  return loaderPromise;
}

export class PaymentCancelledError extends Error {
  constructor() {
    super('Payment cancelled');
    this.name = 'PaymentCancelledError';
  }
}

/** Open Razorpay Checkout and resolve with the success fields, or reject with
 *  PaymentCancelledError if the user dismisses the modal. */
export async function openRazorpayCheckout(
  order: RazorpayOrderLike,
  prefill: RazorpayPrefill,
  description = 'Metro ticket'
): Promise<RazorpaySuccess> {
  await loadCheckout();
  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Razorpay Checkout unavailable');

  return new Promise<RazorpaySuccess>((resolve, reject) => {
    let settled = false;
    const rzp = new Razorpay({
      key: order.keyId,
      order_id: order.razorpayOrderId,
      amount: order.amountPaise,
      currency: order.currency,
      name: 'WayQ Tickets',
      description,
      prefill,
      theme: { color: '#f5c518' },
      handler: (response) => {
        settled = true;
        resolve(response);
      },
      modal: {
        ondismiss: () => {
          if (!settled) reject(new PaymentCancelledError());
        },
      },
    });
    rzp.open();
  });
}
